import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import { createMuiTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';
import { useTheme, useMediaQuery } from '@material-ui/core';
import * as Yup from 'yup';
import { LoadingOutlined } from '@ant-design/icons';
import Grid from '@material-ui/core/Grid';
import DateFnsUtils from '@date-io/date-fns';
import amber from '@material-ui/core/colors/amber';
import CurrencyInput from 'react-currency-input-field';
import {
  useLocation
} from "react-router-dom";

import { apiUrl } from '../../helpers/utils'

import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker
} from '@material-ui/pickers';
import ProfileForm from './forms/ProfileForm';
import RegistryForm from './forms/RegistryForm';
import PayoutSetup from './forms/PayoutSetup';
import FormInviteStep from '../invite';
import { FormSuccess } from './forms/FormSuccess';
import { StepButton } from './StepButton';
import { createProfile } from '../../redux/actions/profileActions';
import { updateRegistry } from '../../redux/actions/registryActions';
import 'date-fns';
import {
  useDocumentTitle, useFileHandler, useModal, useScrollTop
} from '../../hooks';
import { setLoading } from '../../redux/actions/miscActions';
import { INVITE } from '../../constants/routes';
import { history } from '../../routers/AppRouter';

const customTheme = createMuiTheme({
  palette: {
    primary: amber
  }
});


const FormSchema = Yup.object().shape({
  firstName: Yup.string()
    .max(60, 'First name should be less than 60 characters long.')
    .required('First name is required'),
  lastName: Yup.string()
    .max(60, 'Last name should be less than 60 characters long.')
    .required('Last name is required'),
  email: Yup.string()
    .email('Email is not valid.')
    .required('Email is required.'),
  address: Yup.string()
    .required('Address is required to send registry gifts.'),
  mobile: Yup.object()
    .shape({
      country: Yup.string(),
      countryCode: Yup.string(),
      dialCode: Yup.string(),
      value: Yup.string()
    })
});

const today = new Date();
const tomorrow = new Date(today);
tomorrow.setDate(tomorrow.getDate() + 1)

function toDateTime(secs) {
  var t = new Date(1970, 0, 1); // Epoch
  t.setSeconds(secs);
  return t;
}

const MultiStep = () => {
  const theme = useTheme();
  let location = useLocation();
  const fund = location.state && location.state.fund || false
  const editFundRequest = location.state && location.state.editFundRequest || false
  
  const useStyles = makeStyles((theme) => ({
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginTop: '7vh',
      height: '84vh',
      overflow: 'scroll'
    },
    nextButton: {
      margin: 'auto',
      marginBottom: '8px',
      width: '200px'
    },
    start: {
      display: 'grid',
      justifyContent: 'center',
      position: 'fixed',
      top: '50%',
      left: '50%',
      /* bring your own prefixes */
      transform: 'translate(-50%, -50%)',
      '@media (max-width: 768px)' : {
        marginTop: '0'
      }
    },
    shop: {
      marginTop: '25vh',
      '@media (max-width: 768px)' : {
        marginTop: '40%',
        padding: '0px 10px',
        textAlign: 'center'
      }
    },
    title: {
      fontFamily: 'raleway',
      color: '#36b0c9',
      display: 'grid',
      justifyContent: 'center',
      marginTop: '100px',
      '@media (max-width: 768px)' : {
        textAlign: 'center',
        marginTop: 'unset'
      }
    },
    subtitle: {
      fontFamily: 'raleway',
      display: 'grid',
      textAlign: 'center',
      fontWeight: '400'
    },
    dontWorry: {
      marginTop: 0
    },
    shoop: {
      marginTop: '12vh',
      '@media (max-width: 768px)' : {
        marginTop: '10vh',
        padding: '0px 10px',
        textAlign: 'center'
      }
    },
  }));
  
  const limit = 10000000000000000;
  const prefix = '$';
  const {
    profile, auth, isLoading, currentRegistry
  } = useSelector((state) => ({
    profile: state.profile,
    auth: state.auth,
    isLoading: state.app.loading
  }));

  const [fundGoalDate, SetFundGoalDate] = React.useState(editFundRequest ? new Date(profile.fundGoalDate) : new Date(tomorrow));
  const modal = useModal();
  const dispatch = useDispatch();
  const [step, setStep] = useState(fund ? 5 : 1);
  const [stripeLink, setStripeLink] = useState('');
  const [startSelected, setStartSelected] = useState(fund ? true : false);
  const [registry, setRegistry] = useState(profile.registry);
  const [banner, setBanner] = useState(profile.banner);
  const [bannerChanged, setBannerChanged] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(profile.banner);
  const [errorMessage, setErrorMessage] = useState('');
  const [className, setClassName] = useState('');
  const [fundGoal, setFundGoal] = useState(profile.fundGoal);
  const [rawValue, setRawValue] = useState(' ');


  const handleDateChange = (date) => {
    SetFundGoalDate(date);
  };
  

  useEffect(() => () => {
    // dispatch(setLoading(true));
  }, []);

  const classes = useStyles();

  useDocumentTitle('Edit Account | My Breakup Registry');
  useScrollTop();

  const initFormikValues = {
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    email: profile.email || '',
    address: profile.address || '',
    mobile: profile.mobile || {}
  };

  const {
    imageFile,
    isFileLoading,
    onFileChange
  } = useFileHandler({ avatar: {}, banner: {} });

  const handleOnValueChange = (value) => {
    setRawValue(value === undefined ? 'undefined' : value || ' ');

    if (!value) {
      setClassName('');
      setFundGoal('');
      return;
    }

    if (Number.isNaN(Number(value))) {
      setErrorMessage('Please enter a valid number');
      setClassName('is-invalid');
      return;
    }

    if (Number(value) > limit) {
      setErrorMessage(`Max: ${prefix}${limit}`);
      setClassName('is-invalid');
      setFundGoal(value);
      return;
    }

    setClassName('is-valid');
    setFundGoal(value);
  };

  const httpRequestGetStripeLink = (id) => {
    dispatch(setLoading(true));
    return fetch(`${apiUrl}/stripe?id=${id}&live=0`)
      .then((response) => {
        const json = response.json();
        if (response.ok) {
          return json;
        }
        return Promise.reject('Something went wrong.');
      })
      .then((json) => {
        setStripeLink(json.accountLink.url);
        dispatch(setLoading(false));
      })
      .catch((e) => {
        console.log('e', e);
        dispatch(setLoading(false));
      });
  };

  const updateProfile = async (form, credentials = {}) => {
    await dispatch(createProfile({
      updates: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        address: form.address,
        mobile: form.mobile,
        avatar: profile.avatar,
        banner,
        fundGoal,
        fundGoalDate,
        fundAmount: 0,
        fundContributors: []
      },
      files: {
        bannerFile: imageFile.banner.file,
        avatarFile: imageFile.avatar.file
      },
      credentials
    }));
    setStep(step + 1);
  };
  
  const editProfile = async (form, credentials = {}) => {
    await dispatch(createProfile({
      updates: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        address: form.address,
        mobile: form.mobile,
        avatar: profile.avatar,
        banner,
        fundGoal,
        fundGoalDate,
      },
      files: {
        bannerFile: imageFile.banner.file,
        avatarFile: imageFile.avatar.file
      },
      credentials
    }));
    setStep(step + 2);
  };

  const onUpdateRegistry = async () => {
    await dispatch(updateRegistry({
      registry
    }));
    setStep(step + 1);
  };

  const onConfirmUpdate = (form, password) => {
    if (password) {
      updateProfile(form, { email: form.email, password });
    }
  };

  const onSubmitUpdate = (form) => {
    console.log(form);
    // check if data has changed
    const fieldsChanged = Object.keys(form).some((key) => profile[key] !== form[key]);

    if (fieldsChanged || (Boolean(imageFile.avatar.file)) || bannerChanged || fundGoal) {
      if (form.email !== profile.email) {
        modal.onOpenModal();
      } else {
        if (editFundRequest){
          editProfile(form)
        } else {
          updateProfile(form);
        }
      }
    } else setStep(step + 1);
  };

  const onSlideChanged = (e) => {
    setCurrentBanner(+e.slide + 1);
  };

  const onSelectBanner = () => {
    setBanner(`${currentBanner}`);
    setBannerChanged(true);
  };


  const renderStep = (step, values, errors, touched) => {
    switch (step) {
      case 1:
        return (
          <ProfileForm
            errors={errors}
            touched={touched}
            imageFile={imageFile}
            isLoading={isLoading}
            onFileChange={onFileChange}
            profile={profile}
            isFileLoading={isFileLoading}
            auth={auth}
            modal={modal}
            onConfirmUpdate={onConfirmUpdate}
            banner={banner}
            setBanner={setBanner}
            currentBanner={currentBanner}
            setCurrentBanner={setCurrentBanner}
            onSelectBanner={onSelectBanner}
            onSlideChanged={onSlideChanged}
          />
        );
      case 2:
        return (
          <div className={classes.shop}>
            <h1 className={classes.title}> Would you like to set up your registry?</h1>
            <p className={classes.subtitle}>
              Let your friends and loved ones know what you need
              <br />
              to make a fresh start!
            </p>

          </div>
        );
      case 3:
        return (
          <RegistryForm
            errors={errors}
            touched={touched}
            profile={profile}
            registry={registry}
            setRegistry={setRegistry}
          />
        );
      case 4:
        return (
          <div className={classes.shop}>
            <h1 className={classes.title}>Ask Your Community for Financial Support!</h1>
            <p className={classes.subtitle}>
              We can connect your account to mybreakupregistry.com
              <br />
              so that your friends and loved ones can donate to you.
            </p>
            <div className="onboard-button-wrapper-fund">
            <button
              type="button"
              className="registry-button onboard-button"
              disabled={isLoading}
              onClick={ () => {
                httpRequestGetStripeLink(auth.id);
                setStep(step + 1);
              }}
            >
              {isLoading ? <LoadingOutlined /> : null}
              {editFundRequest ? 'Edit Funding Request' : 'Set Up A Funding Request'}
            </button>
            <button
                className="registry-button onboard-button"
              color="primary"
              type="submit"
              onClick={() => history.push(INVITE)}
            >
              Don't request Funding
            </button>
            </div>
          </div>
        );
      case 5:
        return (
          <div className={classes.shoop}>
            <h1 className={classes.title}>{editFundRequest ? 'Edit Fund Request' : 'Fund Request Setup'}</h1>
            <p className={classes.subtitle}>
              What is your goal fund amount?
            </p>
            <form className="needs-validation">
              <div className="row">
                <div className="form-group col">
                  <label htmlFor="validationCustom01" style={{ cursor: 'default' }}>Please enter a value</label>
                  <CurrencyInput
                    id="validationCustom01"
                    name="input-1"
                    className={`form-control ${className}`}
                    value={fundGoal}
                    onValueChange={handleOnValueChange}
                    placeholder="Please enter a number"
                    prefix={prefix}
                    step={1}
                    decimalScale={2}
                  />
                  <div className="invalid-feedback">{errorMessage}</div>
                  <div className="disclaimer-setup">* Stripe takes 2.9% of the purchase + $0.30 per transaction and MBUR takes 1.8%</div>
                </div>
              </div>
            </form>
            <br />
            <h3>
              What is your goal funding date?
            </h3>
            <p className={classes.dontWorry}>Don't worry, you'll keep all your funds raised regardless of if you hit your goal!</p>
            <ThemeProvider theme={customTheme}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container justify="space-around">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="MM/dd/yyyy"
                    margin="normal"
                    id="date-picker-inline"
                    value={fundGoalDate}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </Grid>
              </MuiPickersUtilsProvider>
            </ThemeProvider>
          </div>
        );
      case 6:
        return (
          <PayoutSetup
            errors={errors}
            touched={touched}
            profile={profile}
            url={stripeLink}
          />
        );
      case 7:
        return (
          <FormInviteStep
            errors={errors}
            step={step}
            setStep={setStep}
            touched={touched}
          />
        );
        // case 5:
        // return (
        //   <FormFifthStep
        //     errors={errors}
        //     touched={touched}
        //     profile={profile}
        //     registry={registry}
        //     setRegistry={setRegistry}
        //   />
        // );
      default:
        return <ProfileForm errors={errors} touched={touched} />;
    }
  };


  return (
    !startSelected
      ? (
        <div className={classes.start}>
          <h1 className={classes.title}> Its time to turn a new leaf!</h1>
          <p className={classes.subtitle}>
            Let's get this new beginning begun!
            <br />
            In five steps, you'll be ready to set up your registry.
          </p>
          <button
            className={`${classes.nextButton} onboard-button w-100-mobile`}
            disabled={isLoading}
            type="button"
            onClick={() => setStartSelected(true)}
          >
            {isLoading ? <LoadingOutlined /> : null}
            Start
          </button>
        </div>
      )
      : (
        <>
          <Formik
            enableReinitialize
            initialValues={initFormikValues}
            validateOnChange
            validationSchema={FormSchema}
            onSubmit={onSubmitUpdate}
          >
            {({ values, errors, touched }) => (
              <Form className={classes.form}>
                {renderStep(step, values, errors, touched)}
                <StepButton
                  editFundRequest={editFundRequest}
                  step={step}
                  setStep={setStep}
                  className={classes.nextButton}
                  isLoading={isLoading}
                  onUpdateRegistry={onUpdateRegistry}
                />
              </Form>
            )}
          </Formik>
        </>
      )
  );
};

export default MultiStep;
